import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { ConversationHistoryEntry } from '../../shared/models';

@Injectable({
  providedIn: 'root',
})
export class ConversationHistoryService {
  private readonly STORAGE_KEY_PREFIX = 'kyrgyz_translation_history_';
  private currentUserId: string = 'anonymous'; // Default user ID
  private historySubject = new BehaviorSubject<ConversationHistoryEntry[]>([]);
  public history$ = this.historySubject.asObservable();
  private isLoadingHistory = false; // Flag to prevent multiple simultaneous loads
  private isMobile: boolean = false; // Platform detection

  constructor(private platform: Platform) {
    console.log('📝 ConversationHistoryService initialized');
    this.isMobile = this.platform.is('capacitor');
    console.log('📝 Platform detection - isMobile:', this.isMobile);
    // Initialize with empty history - will load when user is set
    this.historySubject.next([]);
  }

  // Set the current user and load their history
  setCurrentUser(userId: string): void {
    console.log('📝 Setting conversation history for user ID:', userId);
    console.log('📝 User ID type:', typeof userId);
    console.log('📝 User ID length:', userId?.length);
    console.log('📝 Previous user ID:', this.currentUserId);

    const previousUserId = this.currentUserId;
    const newUserId = userId || 'anonymous';

    // Only load history if the user actually changed
    if (previousUserId === newUserId) {
      console.log('📝 User unchanged, skipping history reload');
      return;
    }

    // Prevent multiple simultaneous loads
    if (this.isLoadingHistory) {
      console.log(
        '📝 History loading already in progress, skipping duplicate call'
      );
      return;
    }

    this.currentUserId = newUserId;
    this.isLoadingHistory = true;

    console.log('📝 New user ID:', this.currentUserId);
    console.log('📝 Storage key will be:', this.getStorageKey());

    // Load history from persistent storage for this user
    this.loadHistoryFromStorage()
      .catch((error) => {
        console.error('📝 Error loading history:', error);
      })
      .finally(() => {
        this.isLoadingHistory = false;
      });

    console.log(
      `📝 Switched from user "${previousUserId}" to "${this.currentUserId}"`
    );
    console.log(
      `📝 Loaded ${this.historySubject.value.length} conversation entries`
    );
  }

  // Get the localStorage key for the current user
  private getStorageKey(): string {
    return `${this.STORAGE_KEY_PREFIX}${this.currentUserId}`;
  }

  // Get a provider-specific storage key that prevents conflicts between different auth providers
  private getProviderSpecificStorageKey(
    userId: string,
    provider?: string
  ): string {
    if (provider && provider !== 'unknown') {
      // Extract the provider from the user ID if it's in Auth0 format (e.g., "google-oauth2|123" or "facebook|456")
      const authProvider = userId.includes('|')
        ? userId.split('|')[0]
        : provider;
      return `${this.STORAGE_KEY_PREFIX}${authProvider}_${userId}`;
    }
    return `${this.STORAGE_KEY_PREFIX}${userId}`;
  }

  // Try to migrate data from old email-based storage key to new user ID-based key
  private async tryMigrateFromEmailKey(): Promise<string | null> {
    try {
      // If current user ID looks like an email address, try to find data stored with that email
      if (this.currentUserId.includes('@')) {
        const emailKey = `${this.STORAGE_KEY_PREFIX}${this.currentUserId}`;
        console.log('📝 Attempting to migrate from email-based key:', emailKey);

        // Check Capacitor Preferences first
        const result = await Preferences.get({ key: emailKey });
        if (result.value) {
          console.log(
            '📝 Found data in Capacitor Preferences with email key, migrating...'
          );

          // Save to new user ID key
          const newKey = this.getStorageKey();
          await Preferences.set({ key: newKey, value: result.value });

          // Also try localStorage
          try {
            const localStorageData = localStorage.getItem(emailKey);
            if (localStorageData) {
              localStorage.setItem(newKey, localStorageData);
              console.log(
                '📝 Migrated data from email key to user ID key in localStorage'
              );
            }
          } catch (error) {
            console.log('📝 Could not migrate localStorage data:', error);
          }

          // Remove old email-based key
          await Preferences.remove({ key: emailKey });
          try {
            localStorage.removeItem(emailKey);
          } catch (error) {
            console.log('📝 Could not remove old localStorage key:', error);
          }

          console.log(
            '📝 ✅ Successfully migrated conversation history from email to user ID key'
          );
          return result.value;
        }

        // Check localStorage fallback
        try {
          const localStorageData = localStorage.getItem(emailKey);
          if (localStorageData) {
            console.log(
              '📝 Found data in localStorage with email key, migrating...'
            );

            // Save to new user ID key
            const newKey = this.getStorageKey();
            localStorage.setItem(newKey, localStorageData);

            // Also save to Capacitor Preferences
            await Preferences.set({ key: newKey, value: localStorageData });

            // Remove old email-based key
            localStorage.removeItem(emailKey);

            console.log(
              '📝 ✅ Successfully migrated conversation history from email to user ID key'
            );
            return localStorageData;
          }
        } catch (error) {
          console.log('📝 Could not check localStorage for migration:', error);
        }
      }
    } catch (error) {
      console.error('📝 Error during migration from email key:', error);
    }

    return null;
  }

  // Migrate old conversation entries to include inputType field
  private migrateInputType(
    history: ConversationHistoryEntry[]
  ): ConversationHistoryEntry[] {
    return history.map((entry) => ({
      ...entry,
      // Set default inputType to 'speech' for old entries that don't have it
      inputType: entry.inputType || 'speech',
    }));
  }

  // Optimize conversation entries for web storage (remove large audio data)
  private optimizeForWebStorage(
    history: ConversationHistoryEntry[]
  ): ConversationHistoryEntry[] {
    if (this.isMobile) {
      // On mobile, keep all audio data (base64)
      return history;
    }

    // On web, keep base64 data for persistence and blob URLs for immediate playback
    console.log('📝 Optimizing conversation history for web storage...');
    return history.map((entry) => ({
      ...entry,
      // Keep base64 data for persistence across page refreshes
      audioData: entry.audioData,
      recordedAudioData: entry.recordedAudioData,
      originalAudioData: entry.originalAudioData,
      // Keep blob URLs for immediate playback (they'll be cleaned up later if invalid)
      audioUrl: entry.audioUrl,
      recordedAudioUrl: entry.recordedAudioUrl,
      originalAudioUrl: entry.originalAudioUrl,
    }));
  }

  // Clean up invalid blob URLs from conversation history
  private cleanupInvalidBlobUrls(
    history: ConversationHistoryEntry[]
  ): ConversationHistoryEntry[] {
    return history.map((entry) => {
      const cleanedEntry = { ...entry };

      // Remove blob URLs that are no longer valid (they don't persist across sessions)
      // Blob URLs will be recreated from base64 data when needed for playback
      if (cleanedEntry.audioUrl && cleanedEntry.audioUrl.startsWith('blob:')) {
        console.log(
          '📝 Removing invalid blob URL for audioUrl (will recreate from base64):',
          cleanedEntry.audioUrl
        );
        cleanedEntry.audioUrl = undefined;
      }

      if (
        cleanedEntry.recordedAudioUrl &&
        cleanedEntry.recordedAudioUrl.startsWith('blob:')
      ) {
        console.log(
          '📝 Removing invalid blob URL for recordedAudioUrl (will recreate from base64):',
          cleanedEntry.recordedAudioUrl
        );
        cleanedEntry.recordedAudioUrl = undefined;
      }

      if (
        cleanedEntry.originalAudioUrl &&
        cleanedEntry.originalAudioUrl.startsWith('blob:')
      ) {
        console.log(
          '📝 Removing invalid blob URL for originalAudioUrl (will recreate from base64):',
          cleanedEntry.originalAudioUrl
        );
        cleanedEntry.originalAudioUrl = undefined;
      }

      // Clean up corrupted base64 data
      cleanedEntry.audioData = this.validateBase64Data(cleanedEntry.audioData);
      cleanedEntry.recordedAudioData = this.validateBase64Data(
        cleanedEntry.recordedAudioData
      );
      cleanedEntry.originalAudioData = this.validateBase64Data(
        cleanedEntry.originalAudioData
      );

      return cleanedEntry;
    });
  }

  // Validate and clean base64 data
  private validateBase64Data(base64Data?: string): string | undefined {
    if (!base64Data || typeof base64Data !== 'string') {
      return undefined;
    }

    // Handle data URLs
    let base64String = base64Data;
    if (base64String.includes(',')) {
      base64String = base64String.split(',')[1];
    }

    // More lenient base64 validation - only check for obviously invalid characters
    // Base64 can contain A-Z, a-z, 0-9, +, /, and = for padding
    const invalidChars = /[^A-Za-z0-9+/=]/;
    if (invalidChars.test(base64String)) {
      console.warn(
        '📝 Invalid base64 characters detected, removing corrupted data'
      );
      return undefined;
    }

    // Check if the string is too short to be valid base64
    if (base64String.length < 4) {
      console.warn('📝 Base64 string too short, removing');
      return undefined;
    }

    return base64Data;
  }

  // Save cleaned history back to storage
  private async saveCleanedHistoryToStorage(
    cleanedHistory: ConversationHistoryEntry[]
  ): Promise<void> {
    try {
      const storageKey = this.getStorageKey();
      const cleanedData = JSON.stringify(cleanedHistory);

      // Save to both Capacitor Preferences and localStorage
      await Preferences.set({
        key: storageKey,
        value: cleanedData,
      });

      try {
        localStorage.setItem(storageKey, cleanedData);
        console.log('📝 Saved cleaned history to both storage systems');
      } catch (localStorageError) {
        console.warn(
          '📝 Could not save cleaned history to localStorage:',
          localStorageError
        );
      }
    } catch (error) {
      console.error('📝 Error saving cleaned history:', error);
    }
  }

  // Load conversation history from localStorage
  private async loadHistoryFromStorage(): Promise<void> {
    try {
      const storageKey = this.getStorageKey();
      console.log('📝 Loading from persistent storage key:', storageKey);

      // Try Capacitor Preferences first
      const result = await Preferences.get({ key: storageKey });
      console.log('📝 Loading from Capacitor Preferences:', !!result.value);

      let historyData = result.value;

      // If no data found with user ID key, try to migrate from email-based key
      if (!historyData && this.currentUserId !== 'anonymous') {
        historyData = await this.tryMigrateFromEmailKey();
      }

      // If no data in Capacitor Preferences, try localStorage as fallback
      if (!historyData) {
        console.log(
          '📝 No data in Capacitor Preferences, trying localStorage fallback...'
        );
        try {
          historyData = localStorage.getItem(storageKey);
          console.log('📝 Loading from localStorage fallback:', !!historyData);
        } catch (localStorageError) {
          console.log(
            '📝 Could not read from localStorage:',
            localStorageError
          );
        }
      }

      if (historyData) {
        const history = JSON.parse(historyData);
        console.log(
          '📝 Loaded conversation history:',
          history.length,
          'entries'
        );

        // Clean up invalid blob URLs from stored history
        let cleanedHistory = this.cleanupInvalidBlobUrls(history);

        // Migrate old entries that don't have inputType field
        const migratedHistory = this.migrateInputType(cleanedHistory);
        if (
          migratedHistory.length !== cleanedHistory.length ||
          migratedHistory.some(
            (entry, index) =>
              entry.inputType !== cleanedHistory[index]?.inputType
          )
        ) {
          console.log('📝 Migrated inputType for old conversation entries');
          cleanedHistory = migratedHistory;
          // Save the migrated history back to storage
          this.saveCleanedHistoryToStorage(cleanedHistory);
        }

        this.historySubject.next(cleanedHistory);
        console.log(
          `📝 ✅ Loaded ${cleanedHistory.length} conversation entries from persistent storage for user: ${this.currentUserId}`
        );
      } else {
        console.log(
          `📝 ℹ️ No stored conversation history found for user: ${this.currentUserId}`
        );
        this.historySubject.next([]);
      }
    } catch (error) {
      console.error(
        '📝 ❌ Error loading conversation history from persistent storage:',
        error
      );
      this.historySubject.next([]);
    }
  }

  // Save conversation history to persistent storage (Capacitor Preferences)
  private async saveHistoryToStorage(): Promise<void> {
    if (this.isSaving) return; // Prevent multiple simultaneous saves
    this.isSaving = true;

    try {
      const storageKey = this.getStorageKey();
      let history = this.historySubject.value;

      console.log('📝 Saving to persistent storage key:', storageKey);
      console.log('📝 History length:', history.length);
      console.log('📝 Current user ID:', this.currentUserId);
      console.log('📝 Platform - isMobile:', this.isMobile);

      // Optimize for web storage (remove large audio data)
      history = this.optimizeForWebStorage(history);

      // Check if data is too large and clean up if needed
      let dataSize = JSON.stringify(history).length;
      console.log('📝 Initial data size:', dataSize, 'characters');

      // Reduced limit to prevent QuotaExceededError - be more aggressive with cleanup
      const PREVENTIVE_LIMIT = 1.5 * 1024 * 1024; // 1.5MB - trigger cleanup earlier
      const MAX_STORAGE_SIZE = 2 * 1024 * 1024; // 2MB hard limit
      
      if (dataSize > PREVENTIVE_LIMIT) {
        console.warn(
          '📝 Data too large (',
          dataSize,
          'bytes), performing aggressive cleanup...'
        );
        // Use aggressive cleanup to limit entries and remove all audio
        history = this.aggressiveCleanup(history);
        dataSize = JSON.stringify(history).length;
        console.log(
          '📝 After aggressive cleanup - data size:',
          dataSize,
          'characters'
        );
        
        // If still too large, be even more aggressive
        if (dataSize > MAX_STORAGE_SIZE) {
          console.warn('📝 Still too large, performing ultra-aggressive cleanup...');
          history = this.ultraAggressiveCleanup(history);
          dataSize = JSON.stringify(history).length;
          console.log('📝 After ultra-aggressive cleanup - data size:', dataSize, 'characters');
        }
      }

      let historyJson = JSON.stringify(history);

      // Try to save to Capacitor Preferences first
      try {
        await Preferences.set({
          key: storageKey,
          value: historyJson,
        });
      } catch (preferencesError: any) {
        // Handle QuotaExceededError from Preferences.set()
        if (
          preferencesError.message?.includes('QuotaExceededError') ||
          preferencesError.name === 'QuotaExceededError' ||
          preferencesError.code === 22 ||
          preferencesError.code === 1014
        ) {
          console.warn(
            '📝 Preferences QuotaExceededError: Performing ultra-aggressive cleanup...'
          );
          history = this.ultraAggressiveCleanup(history);
          const ultraCleanedJson = JSON.stringify(history);
          this.historySubject.next(history);
          
          // Try again with ultra-cleaned data
          await Preferences.set({
            key: storageKey,
            value: ultraCleanedJson,
          });
          historyJson = ultraCleanedJson; // Update for localStorage save below
        } else {
          throw preferencesError; // Re-throw if not a quota error
        }
      }

      // Also try to save to localStorage as a fallback for iOS
      try {
        localStorage.setItem(storageKey, historyJson);
        console.log('📝 Also saved to localStorage as fallback');
      } catch (localStorageError: any) {
        // Handle QuotaExceededError
        if (
          localStorageError.name === 'QuotaExceededError' ||
          localStorageError.code === 22 ||
          localStorageError.code === 1014
        ) {
          console.warn(
            '📝 QuotaExceededError: Storage quota exceeded, performing aggressive cleanup...'
          );

          // Aggressive cleanup - keep only recent entries without audio
          history = this.aggressiveCleanup(history);
          const cleanedJson = JSON.stringify(history);

          try {
            // Update the BehaviorSubject with cleaned history
            this.historySubject.next(history);

            // Try saving again with cleaned data
            localStorage.setItem(storageKey, cleanedJson);
            await Preferences.set({
              key: storageKey,
              value: cleanedJson,
            });
            console.log(
              '📝 ✅ Saved after aggressive cleanup - history length:',
              history.length
            );
          } catch (retryError) {
            console.error(
              '📝 ❌ Failed to save even after aggressive cleanup:',
              retryError
            );
            // Last resort: use ultra-aggressive cleanup
            history = this.ultraAggressiveCleanup(history);
            const minimalJson = JSON.stringify(history);
            this.historySubject.next(history);
            
            try {
              localStorage.setItem(storageKey, minimalJson);
              await Preferences.set({
                key: storageKey,
                value: minimalJson,
              });
              console.log(
                '📝 ✅ Saved minimal history (5 entries, text truncated) as last resort'
              );
            } catch (finalError) {
              console.error('📝 ❌ Even ultra-aggressive cleanup failed:', finalError);
              // At this point, we can't save - user might need to manually clear storage
            }
          }
        } else {
          console.log('📝 Could not save to localStorage:', localStorageError);
        }
      }

      // Verify it was saved
      const verification = await Preferences.get({ key: storageKey });
      console.log('📝 Verification - saved data exists:', !!verification.value);
      console.log(
        '📝 Verification - saved data length:',
        verification.value?.length || 0
      );

      // Also verify localStorage
      const localStorageVerification = localStorage.getItem(storageKey);
      console.log(
        '📝 localStorage verification - saved data exists:',
        !!localStorageVerification
      );
      console.log(
        '📝 localStorage verification - saved data length:',
        localStorageVerification?.length || 0
      );

      console.log(
        `📝 ✅ Saved ${history.length} conversation entries to persistent storage for user: ${this.currentUserId}`
      );
    } catch (error) {
      console.error(
        '📝 ❌ Error saving conversation history to persistent storage:',
        error
      );

      // Fallback to localStorage if Capacitor Preferences fails
      try {
        const storageKey = this.getStorageKey();
        let history = this.historySubject.value;

        // Clean up data before fallback storage - use smaller limit for localStorage
        const dataSize = JSON.stringify(history).length;
        const MAX_LOCALSTORAGE_SIZE = 1.5 * 1024 * 1024; // 1.5MB limit for fallback storage
        if (dataSize > MAX_LOCALSTORAGE_SIZE) {
          console.warn(
            '📝 Fallback: Data too large, performing ultra-aggressive cleanup...'
          );
          // Use ultra-aggressive cleanup to remove audio and truncate text
          history = this.ultraAggressiveCleanup(history);
          this.historySubject.next(history);
        }

        localStorage.setItem(storageKey, JSON.stringify(history));
        console.log('📝 Fallback: Saved to localStorage instead');
      } catch (fallbackError: any) {
        console.error('📝 Fallback localStorage also failed:', fallbackError);
        
        // Check if it's a QuotaExceededError
        if (
          fallbackError.name === 'QuotaExceededError' ||
          fallbackError.code === 22 ||
          fallbackError.code === 1014
        ) {
          // Last resort: use ultra-aggressive cleanup
          try {
            console.warn('📝 Last resort: Performing ultra-aggressive cleanup...');
            const storageKey = this.getStorageKey();
            let history = this.ultraAggressiveCleanup(this.historySubject.value);
            this.historySubject.next(history);
            localStorage.setItem(storageKey, JSON.stringify(history));
            console.log('📝 Last resort: Saved with ultra-aggressive cleanup');
          } catch (finalError) {
            console.error('📝 All storage attempts failed:', finalError);
            // At this point, we can't save - user might need to manually clear storage
          }
        } else {
          console.error('📝 Fallback error is not a quota error:', fallbackError);
        }
      }
    } finally {
      this.isSaving = false; // Reset flag when done
    }
  }

  // Clean up history to reduce storage size
  // ⚡ PERFORMANCE: Only limit number of entries, keep ALL audio data
  private cleanupHistory(
    history: ConversationHistoryEntry[]
  ): ConversationHistoryEntry[] {
    // Keep only the most recent 50 entries (increased from 20)
    // Don't remove any audio data - let users decide when to clean up
    return history.slice(0, 50);
  }

  /**
   * Aggressive cleanup when storage quota is exceeded
   * Strategy: Remove ALL audio data, keep only text
   * Rationale: When storage is critically low, text is most important
   */
  private aggressiveCleanup(
    history: ConversationHistoryEntry[]
  ): ConversationHistoryEntry[] {
    console.log(
      '📝 Performing aggressive cleanup - removing ALL audio, keeping text only'
    );

    // Keep 10 most recent entries (reduced from 15 to prevent quota errors)
    // Remove ALL audio data to ensure we can save
    return history.slice(0, 10).map((entry) => ({
      ...entry,
      // ❌ Remove ALL audio data to prevent QuotaExceededError
      recordedAudioData: '',
      recordedAudioUrl: '',
      audioData: '',
      audioUrl: '',
      originalAudioData: '',
      originalAudioUrl: '',
      // ✅ Keep all text data
      // originalText: preserved
      // translatedText: preserved
      // Users can use "Generate Audio" buttons to regenerate any audio if needed
    }));
  }

  /**
   * Ultra-aggressive cleanup when storage quota is critically exceeded
   * Strategy: Keep only 5 most recent entries, remove ALL audio, truncate text
   * Rationale: When even aggressive cleanup isn't enough, preserve only essential data
   */
  private ultraAggressiveCleanup(
    history: ConversationHistoryEntry[]
  ): ConversationHistoryEntry[] {
    console.log(
      '📝 Performing ultra-aggressive cleanup - keeping only 5 entries, removing ALL audio, truncating text'
    );

    // Keep only 5 most recent entries
    // Remove ALL audio data AND truncate text to prevent storage issues
    return history.slice(0, 5).map((entry) => ({
      ...entry,
      // ❌ Remove ALL audio data
      recordedAudioData: '',
      recordedAudioUrl: '',
      audioData: '',
      audioUrl: '',
      originalAudioData: '',
      originalAudioUrl: '',
      // ⚠️ Truncate text to prevent extremely long entries from causing issues
      originalText: entry.originalText?.substring(0, 500) || '',
      translatedText: entry.translatedText?.substring(0, 500) || '',
    }));
  }

  private isSaving = false; // Flag to prevent multiple simultaneous saves

  // Proactive cleanup to prevent storage quota errors
  // ⚡ PERFORMANCE: Only cleanup when absolutely necessary
  private async proactiveCleanup(): Promise<void> {
    if (this.isSaving) return; // Prevent multiple simultaneous saves

    try {
      const history = this.historySubject.value;
      const dataSize = JSON.stringify(history).length;
      const PREVENTIVE_LIMIT = 1.5 * 1024 * 1024; // 1.5MB - start cleaning earlier to prevent quota errors

      // Only cleanup if exceeding limit (limit entries and remove audio)
      if (dataSize > PREVENTIVE_LIMIT) {
        console.log(
          '📝 Proactive cleanup: Data size exceeding 1.5MB limit, performing aggressive cleanup...'
        );
        const cleanedHistory = this.aggressiveCleanup(history);
        this.historySubject.next(cleanedHistory);
        await this.saveHistoryToStorage();
      }
    } catch (error) {
      console.warn('📝 Proactive cleanup failed:', error);
    }
  }

  addConversation(
    entry: Omit<ConversationHistoryEntry, 'id' | 'timestamp'>
  ): void {
    console.log('📝 Adding conversation for user:', this.currentUserId);
    console.log('📝 Entry summary:', {
      originalText:
        entry.originalText?.substring(0, 50) +
        (entry.originalText?.length > 50 ? '...' : ''),
      translatedText:
        entry.translatedText?.substring(0, 50) +
        (entry.translatedText?.length > 50 ? '...' : ''),
      userLanguage: entry.userLanguage,
      targetLanguage: entry.targetLanguage,
      inputType: entry.inputType,
      hasAudio: !!entry.audioUrl,
    });

    const newEntry: ConversationHistoryEntry = {
      ...entry,
      id: this.generateId(),
      timestamp: Date.now(),
      // Ensure inputType is set - default to 'speech' if not specified
      inputType: entry.inputType || 'speech',
    };

    const currentHistory = this.historySubject.value;
    const updatedHistory = [newEntry, ...currentHistory]; // Add to beginning for newest first

    console.log('📝 Updated history length:', updatedHistory.length);

    // Optimize for web storage before saving
    const optimizedHistory = this.optimizeForWebStorage(updatedHistory);
    this.historySubject.next(optimizedHistory);

    // Proactive cleanup to prevent storage quota errors
    this.proactiveCleanup();

    // Save to persistent storage
    this.saveHistoryToStorage().catch((error) => {
      console.error('📝 Error saving history:', error);
    });
  }

  getConversations(): ConversationHistoryEntry[] {
    return this.historySubject.value;
  }

  getConversationsSortedByTime(): ConversationHistoryEntry[] {
    return [...this.historySubject.value].sort(
      (a, b) => b.timestamp - a.timestamp
    );
  }

  searchConversations(query: string): ConversationHistoryEntry[] {
    if (!query.trim()) {
      return this.getConversationsSortedByTime();
    }

    const searchTerm = query.toLowerCase().trim();
    return this.getConversationsSortedByTime().filter(
      (entry) =>
        entry.originalText.toLowerCase().includes(searchTerm) ||
        entry.translatedText.toLowerCase().includes(searchTerm) ||
        entry.userLanguage.toLowerCase().includes(searchTerm) ||
        entry.targetLanguage.toLowerCase().includes(searchTerm)
    );
  }

  deleteConversation(id: string): void {
    const currentHistory = this.historySubject.value;
    const updatedHistory = currentHistory.filter((entry) => entry.id !== id);
    this.historySubject.next(updatedHistory);
    // Save to persistent storage
    this.saveHistoryToStorage().catch((error) => {
      console.error('📝 Error saving history:', error);
    });
  }

  updateEntry(updatedEntry: ConversationHistoryEntry): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const currentHistory = this.historySubject.value;
        const updatedHistory = currentHistory.map((entry) =>
          entry.id === updatedEntry.id ? updatedEntry : entry
        );
        this.historySubject.next(updatedHistory);

        // Save to persistent storage
        this.saveHistoryToStorage()
          .then(() => resolve())
          .catch((error) => {
            console.error('📝 Error saving history:', error);
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  clearAllHistory(): void {
    this.historySubject.next([]);
    // Save to persistent storage
    this.saveHistoryToStorage().catch((error) => {
      console.error('📝 Error saving history:', error);
    });
  }

  /**
   * Clear corrupted conversation history data
   * Use this when you see fake translations you didn't create
   */
  async clearCorruptedHistory(): Promise<void> {
    console.log('🧹 Clearing corrupted conversation history...');

    // Clear from memory
    this.historySubject.next([]);

    // Clear from both storage locations
    const storageKey = this.getStorageKey();

    // Clear from Capacitor Preferences
    await Preferences.remove({ key: storageKey });

    // Clear from localStorage
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.log('🧹 Could not remove from localStorage:', error);
    }

    console.log('🧹 ✅ Corrupted conversation history cleared');
    console.log('🧹 Storage key removed from both locations:', storageKey);
  }

  /**
   * Debug method to inspect persistent storage contents
   */
  async debugLocalStorage(): Promise<void> {
    console.log(
      '🔍 Debug: Inspecting persistent storage for conversation history...'
    );

    const storageKey = this.getStorageKey();
    console.log('🔍 Storage key:', storageKey);
    console.log('🔍 Current user ID:', this.currentUserId);

    // Check Capacitor Preferences
    const result = await Preferences.get({ key: storageKey });
    const capacitorData = result.value;

    console.log(
      '🔍 Capacitor Preferences data:',
      !!capacitorData,
      `(${capacitorData?.length || 0} chars)`
    );

    // Check localStorage
    let localStorageData = null;
    try {
      localStorageData = localStorage.getItem(storageKey);
    } catch (error) {
      console.log('🔍 Could not access localStorage:', error);
    }

    console.log('🔍 localStorage - Raw data exists:', !!localStorageData);
    console.log(
      '🔍 localStorage - Raw data length:',
      localStorageData?.length || 0
    );

    // Try to parse Capacitor Preferences data
    if (capacitorData) {
      try {
        const parsedData = JSON.parse(capacitorData);
        console.log('🔍 Capacitor Preferences - Parsed data:', parsedData);
        console.log(
          '🔍 Capacitor Preferences - Number of entries:',
          parsedData?.length || 0
        );

        if (Array.isArray(parsedData) && parsedData.length > 0) {
          console.log('🔍 Capacitor Preferences - First entry:', parsedData[0]);
          console.log(
            '🔍 Capacitor Preferences - Last entry:',
            parsedData[parsedData.length - 1]
          );
        }
      } catch (error) {
        console.error('🔍 Error parsing Capacitor Preferences data:', error);
      }
    }

    // Try to parse localStorage data
    if (localStorageData) {
      try {
        const parsedData = JSON.parse(localStorageData);
        console.log('🔍 localStorage - Parsed data:', parsedData);
        console.log(
          '🔍 localStorage - Number of entries:',
          parsedData?.length || 0
        );

        if (Array.isArray(parsedData) && parsedData.length > 0) {
          console.log('🔍 localStorage - First entry:', parsedData[0]);
          console.log(
            '🔍 localStorage - Last entry:',
            parsedData[parsedData.length - 1]
          );
        }
      } catch (error) {
        console.error('🔍 Error parsing localStorage data:', error);
      }
    }

    // Show current in-memory data
    console.log(
      '🔍 In-memory history length:',
      this.historySubject.value.length
    );
    console.log('🔍 In-memory history:', this.historySubject.value);
  }

  getConversationById(id: string): ConversationHistoryEntry | undefined {
    return this.historySubject.value.find((entry) => entry.id === id);
  }

  getTotalConversations(): number {
    return this.historySubject.value.length;
  }

  getTotalDuration(): number {
    return this.historySubject.value.reduce(
      (total, entry) => total + (entry.duration || 0),
      0
    );
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Convert audio URL to base64 data for persistent storage
  async convertAudioUrlToBase64(audioUrl: string): Promise<string | null> {
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting audio to base64:', error);
      return null;
    }
  }

  // Convert base64 data back to blob URL for playback
  convertBase64ToBlobUrl(base64Data: string): string {
    if (!base64Data || typeof base64Data !== 'string') {
      console.warn('🎙️ Invalid base64 data provided:', typeof base64Data);
      return '';
    }

    try {
      let base64String = base64Data;

      // Handle data URLs (e.g., "data:audio/mpeg;base64,//PExAB...")
      if (base64String.includes(',')) {
        base64String = base64String.split(',')[1];
      }

      // Validate base64 string format
      if (!base64String || base64String.length === 0) {
        console.warn('🎙️ Empty base64 string after processing');
        return '';
      }

      // Check if the string contains valid base64 characters
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(base64String)) {
        console.warn(
          '🎙️ Invalid base64 format:',
          base64String.substring(0, 50) + '...'
        );
        return '';
      }

      // Decode base64 string
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Create blob and URL
      const blob = new Blob([byteArray], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);

      console.log('🎙️ Successfully converted base64 to blob URL');
      return blobUrl;
    } catch (error) {
      console.error('🎙️ Error converting base64 to blob URL:', error);
      console.error(
        '🎙️ Base64 data preview:',
        base64Data?.substring(0, 100) + '...'
      );
      return '';
    }
  }

  // Export history as JSON
  exportHistory(): string {
    return JSON.stringify(this.getConversationsSortedByTime(), null, 2);
  }

  // Import history from JSON
  importHistory(jsonData: string): boolean {
    try {
      const importedHistory = JSON.parse(jsonData);
      if (Array.isArray(importedHistory)) {
        this.historySubject.next(importedHistory);
        // Save to localStorage
        this.saveHistoryToStorage();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error importing conversation history:', error);
      return false;
    }
  }
}
